import { applyImplementationPath } from "@/data/elevateImplementationPath";
import { AI_SETUP_CHOICES, AI_PILOT_STEPS, AI_TEACH_BACK } from "@/data/aiWorkflowFoundations";
import curriculumJson from "@/data/s2pElevateCurriculum.json";
import curriculumV2Json from "@/data/s2pElevateCurriculumV2.json";
import sessionThreeMastermindJson from "@/data/s2pElevateSession3Mastermind.json";
import sessionFiveMetricLoopJson from "@/data/s2pElevateSession5MetricLoop.json";
import { CHIEF_OF_STAFF_OPERATING_BRIEF } from "@/data/chiefOfStaffOperatingBrief.js";
import { applyCoreShowcase } from "@/data/s2pElevateShowcaseExamples";

export type S2PElevateSessionId = 1 | 2 | 3 | 4 | 5;
export type S2PElevateTone = "navy" | "paper" | "blue" | "cloud";
export type S2PElevateSlideKind =
  | "cover"
  | "outcomes"
  | "concept"
  | "compare"
  | "levels"
  | "gear-system"
  | "prompt-explorer"
  | "decision-rights"
  | "ease-impact"
  | "prompt"
  | "discussion"
  | "activity"
  | "session"
  | "showcase-sequence"
  | "showcase-capability"
  | "showcase-applications"
  | "showcase-example"
  | "record-skill"
  | "agent-setup"
  | "ai-foundation"
  | "swot-library"
  | "tool-loop"
  | "use-case-library"
  | "finish";

export interface S2PElevateSession {
  id: S2PElevateSessionId;
  label: string;
  title: string;
  minutes: number;
  coreMinutes: number;
  exerciseMinutes: number;
  discussionMinutes: number;
  outcome: string;
  teachingPlan?: Array<{ title: string; minutes: number }>;
}

export interface S2PElevateCheckpoint {
  id: string;
  session: S2PElevateSessionId;
  title: string;
}

export interface S2PElevateSection {
  label?: string;
  title: string;
  body: string;
  prompt?: string;
}

export interface S2PElevateApplication {
  function: string;
  use: string;
}

export interface S2PElevateSupportRow {
  provider: string;
  status: string;
  detail: string;
  tone: "yes" | "partial" | "no";
}

export interface S2PElevateShowcase {
  id: string;
  platform: string;
  category: string;
  title: string;
  lead: string;
  bullets: string[];
  agentic: string;
  implication: string;
  applications: S2PElevateApplication[];
  support: S2PElevateSupportRow[];
  image?: string;
  imageAlt?: string;
  sourceUrl?: string;
  sourceIds?: string[];
  example?: S2PElevateShowcaseExample;
}

export interface S2PElevateShowcaseExample {
  title: string;
  inputs: string;
  work: string;
  review: string;
  done: string;
  artifact: { title: string; lines: [string, string, string] };
  applicationFunctions: [string, string, string];
  practice?: S2PElevatePractice;
}

export interface S2PElevatePractice {
  title: string;
  decisionPause?: string;
  offline?: { instructions: string; outputs: Array<{ label: string; text: string }> };
  promptLabel?: string;
  prompt: string;
  sampleInput: string;
  followUp?: { label: string; instructions: string; prompt: string };
  expectedOutput: string;
  sources?: Array<{ label: string; href: string }>;
}

export interface S2PElevateSlide {
  id: string;
  session: S2PElevateSessionId;
  kind: S2PElevateSlideKind;
  tone: S2PElevateTone;
  title: string;
  eyebrow?: string;
  durationMinutes?: number;
  gearFocus?: "all" | "lower" | "upper";
  lead?: string;
  quote?: string;
  bullets?: string[];
  questions?: string[];
  sections?: S2PElevateSection[];
  promptLabel?: string;
  prompt?: string;
  note?: string;
  warning?: string;
  checkpointId?: string;
  checkpoint?: string;
  sourceIds: string[];
  sourceUrl?: string;
  sourceLabel?: string;
  showcaseId?: string;
  showcaseIds?: string[];
  practice?: S2PElevatePractice;
  opening?: { focus: string; progress: string; participation: string };
  agentGuide?: {
    view: "trigger" | "brief" | "setup" | "save" | "run" | "exception" | "instructions" | "schedule" | "manage";
    walkthroughs?: Array<{
      platform: "chatgpt" | "claude" | "copilot";
      label: string;
      path: string;
      steps: string[];
      hint: string;
      sourceUrl: string;
      sourceLabel: string;
    }>;
  };
  debrief?: { timing: string; share: string; transfer: string; commitment: string };
}

export interface S2PElevateCurriculum {
  course: {
    title: string;
    subtitle: string;
    totalMinutes: number;
    guideHref?: string;
  };
  sessions: S2PElevateSession[];
  checkpoints: S2PElevateCheckpoint[];
  showcase: S2PElevateShowcase[];
  slides: S2PElevateSlide[];
}

interface S2PElevateSessionOverlay {
  session: S2PElevateSession;
  checkpoints: S2PElevateCheckpoint[];
  slides: S2PElevateSlide[];
}

type S2PElevateSlideOverride = {
  [Key in keyof S2PElevateSlide]?: S2PElevateSlide[Key] | null;
};

interface S2PElevateCurriculumManifest {
  course: S2PElevateCurriculum["course"];
  sessions: S2PElevateSession[];
  checkpoints: S2PElevateCheckpoint[];
  slideSequence: string[];
  slideOverrides: Record<string, S2PElevateSlideOverride>;
  newSlides: S2PElevateSlide[];
}

export interface S2PElevateProgram {
  curriculum: S2PElevateCurriculum;
  sessions: S2PElevateSession[];
  checkpoints: S2PElevateCheckpoint[];
  showcase: S2PElevateShowcase[];
  slides: S2PElevateSlide[];
  totalMinutes: number;
}

export const createS2PElevateProgram = (curriculum: S2PElevateCurriculum): S2PElevateProgram => {
  const slides = curriculum.slides.flatMap((slide) => {
    if (slide.kind !== "showcase-sequence") return [slide];

    return curriculum.showcase.flatMap((item, index) => [
      {
        id: `showcase-${item.id}`,
        session: slide.session,
        kind: "showcase-capability" as const,
        tone: index % 2 === 0 ? "paper" as const : "cloud" as const,
        eyebrow: `Showcase ${index + 1} of ${curriculum.showcase.length} · ${item.platform}`,
        title: item.title,
        lead: item.lead,
        showcaseId: item.id,
        sourceIds: item.sourceIds ?? ["showcase", `showcase.${item.id}`],
      },
      {
        id: `showcase-${item.id}-across-business`,
        session: slide.session,
        kind: "showcase-applications" as const,
        tone: index % 2 === 0 ? "navy" as const : "blue" as const,
        eyebrow: `Showcase ${index + 1} of ${curriculum.showcase.length} · Apply across the business`,
        title: item.title,
        lead: item.implication,
        showcaseId: item.id,
        sourceIds: item.sourceIds ?? ["showcase", `showcase.${item.id}`],
      },
    ]);
  });

  return {
    curriculum,
    sessions: curriculum.sessions,
    checkpoints: curriculum.checkpoints,
    showcase: curriculum.showcase,
    slides,
    totalMinutes: curriculum.sessions.reduce((sum, session) => sum + session.minutes, 0),
  };
};

const baseCurriculum = curriculumJson as S2PElevateCurriculum;
const sessionThreeMastermind = sessionThreeMastermindJson as S2PElevateSessionOverlay;
const sessionFiveMetricLoop = sessionFiveMetricLoopJson as Pick<S2PElevateSessionOverlay, "slides">;
const curriculumV2 = curriculumV2Json as S2PElevateCurriculumManifest;
const curriculumV2Slides = curriculumV2.newSlides.map((slide) => (
  slide.id === "chief-of-staff-operating-brief"
    ? { ...slide, prompt: CHIEF_OF_STAFF_OPERATING_BRIEF }
    : slide
));

const sourceSlides = [
  ...baseCurriculum.slides,
  ...sessionThreeMastermind.slides,
  ...sessionFiveMetricLoop.slides,
  ...curriculumV2Slides,
];
const sourceSlidesById = new Map(sourceSlides.map((slide) => [slide.id, slide]));

const applySlideOverride = (
  slide: S2PElevateSlide,
  override: S2PElevateSlideOverride = {},
): S2PElevateSlide => {
  const merged = {
    ...slide,
    ...override,
    sourceIds: [...new Set([...(slide.sourceIds ?? []), ...((override.sourceIds as string[] | null) ?? [])])],
  } as unknown as Record<string, unknown>;

  for (const [key, value] of Object.entries(override)) {
    if (value === null) delete merged[key];
  }

  return merged as unknown as S2PElevateSlide;
};

const missingSlideIds = curriculumV2.slideSequence.filter((id) => !sourceSlidesById.has(id));
if (missingSlideIds.length > 0) {
  throw new Error(`The ELEVATE v2 manifest references missing slides: ${missingSlideIds.join(", ")}`);
}

export const S2P_ELEVATE_BASE_CURRICULUM: S2PElevateCurriculum = {
  course: curriculumV2.course,
  sessions: curriculumV2.sessions,
  checkpoints: curriculumV2.checkpoints,
  showcase: baseCurriculum.showcase,
  slides: curriculumV2.slideSequence.map((id) =>
    applySlideOverride(sourceSlidesById.get(id)!, {
      ...curriculumV2.slideOverrides[id],
      ...(id === "ai-setup-choices" ? { sections: AI_SETUP_CHOICES } : {}),
      ...(id === "ai-workflow-pilot" ? { sections: AI_PILOT_STEPS } : {}),
      ...(id === "qa-1" ? { questions: AI_TEACH_BACK } : {}),
    }),
  ),
};
export const S2P_ELEVATE_CURRICULUM: S2PElevateCurriculum = {
  ...S2P_ELEVATE_BASE_CURRICULUM,
  showcase: baseCurriculum.showcase.map(applyCoreShowcase),
  slides: S2P_ELEVATE_BASE_CURRICULUM.slides.map((slide) => applyImplementationPath(slide)),
};
export const S2P_ELEVATE_PROGRAM = createS2PElevateProgram(S2P_ELEVATE_CURRICULUM);
export const S2P_ELEVATE_SESSIONS = S2P_ELEVATE_PROGRAM.sessions;
export const S2P_ELEVATE_CHECKPOINTS = S2P_ELEVATE_PROGRAM.checkpoints;
export const S2P_ELEVATE_SHOWCASE = S2P_ELEVATE_PROGRAM.showcase;
export const S2P_ELEVATE_SLIDES = S2P_ELEVATE_PROGRAM.slides;
export const S2P_ELEVATE_TOTAL_MINUTES = S2P_ELEVATE_PROGRAM.totalMinutes;
